/**
 * Project Activity Log Component
 *
 * Displays a chronological log of project changes including:
 * - Milestone completions/uncompletion
 * - Status changes
 * - Project updates
 * - Reschedules
 */

import { History, CheckCircle2, XCircle, Calendar, FileEdit, Archive, ArchiveRestore, PlusCircle, RefreshCw, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuditLogs } from '../hooks/useProjects';
import type { AuditLogEntry } from '../api';

interface ProjectActivityLogProps {
  projectId: string;
}

// Helper to format relative time
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

// Helper to format full date/time
function formatFullDateTime(timestamp: string): string {
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Get icon and color for each action type
function getActionDisplay(action: string): { icon: React.ElementType; color: string; label: string } {
  switch (action) {
    case 'project_created':
      return { icon: PlusCircle, color: 'text-green-600', label: 'Project created' };
    case 'project_updated':
      return { icon: FileEdit, color: 'text-blue-600', label: 'Project updated' };
    case 'project_notes_updated':
      return { icon: FileText, color: 'text-indigo-600', label: 'Notes updated' };
    case 'project_rescheduled':
      return { icon: Calendar, color: 'text-orange-600', label: 'Rescheduled' };
    case 'project_archived':
      return { icon: Archive, color: 'text-gray-600', label: 'Archived' };
    case 'project_unarchived':
      return { icon: ArchiveRestore, color: 'text-gray-600', label: 'Unarchived' };
    case 'milestone_completed':
      return { icon: CheckCircle2, color: 'text-green-600', label: 'Milestone completed' };
    case 'milestone_uncompleted':
      return { icon: XCircle, color: 'text-yellow-600', label: 'Milestone reopened' };
    case 'status_changed':
      return { icon: RefreshCw, color: 'text-purple-600', label: 'Status changed' };
    default:
      return { icon: History, color: 'text-muted-foreground', label: action };
  }
}

// Format action details into a readable string
function formatDetails(action: string, details: Record<string, unknown> | undefined): string | null {
  if (!details) return null;

  switch (action) {
    case 'milestone_completed':
    case 'milestone_uncompleted':
      return details.milestoneName as string || details.milestoneKey as string || null;
    case 'project_rescheduled':
      if (details.oldDate && details.newDate) {
        return `${details.oldDate} \u2192 ${details.newDate}`;
      }
      return null;
    case 'status_changed':
      if (details.oldStatus && details.newStatus) {
        return `${details.oldStatus} \u2192 ${details.newStatus}`;
      }
      return null;
    case 'project_updated':
      // Show what fields were changed
      if (details.changes && typeof details.changes === 'object') {
        const changedFields = Object.keys(details.changes as object);
        if (changedFields.length > 0) {
          return changedFields.slice(0, 3).join(', ') + (changedFields.length > 3 ? '...' : '');
        }
      }
      return null;
    case 'project_notes_updated':
      if (details.changeType === 'added') {
        return 'Added notes';
      }
      if (details.changeType === 'removed') {
        return 'Removed notes';
      }
      if (details.changeType === 'updated') {
        return 'Edited notes';
      }
      return null;
    default:
      return null;
  }
}

function getNotesDiff(details: Record<string, unknown> | undefined): { oldText: string; newText: string } | null {
  if (!details) return null;
  const oldValue = details.oldNotes;
  const newValue = details.newNotes;
  if (typeof oldValue !== 'string' && oldValue !== null) return null;
  if (typeof newValue !== 'string' && newValue !== null) return null;

  return {
    oldText: typeof oldValue === 'string' && oldValue.length > 0 ? oldValue : '(empty)',
    newText: typeof newValue === 'string' && newValue.length > 0 ? newValue : '(empty)',
  };
}

function ActivityLogItem({ log }: { log: AuditLogEntry }) {
  const { icon: Icon, color, label } = getActionDisplay(log.action);
  const detailsText = formatDetails(log.action, log.details);
  const notesDiff = log.action === 'project_notes_updated' ? getNotesDiff(log.details) : null;

  return (
    <div className="flex gap-3 py-2">
      <div className={`shrink-0 mt-0.5 ${color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">{label}</span>
          <span
            className="text-xs text-muted-foreground shrink-0"
            title={formatFullDateTime(log.timestamp)}
          >
            {formatRelativeTime(log.timestamp)}
          </span>
        </div>
        {detailsText && (
          <p className="text-sm text-muted-foreground truncate">{detailsText}</p>
        )}
        {notesDiff && (
          <div className="mt-2 rounded-md border bg-muted/30 p-2 text-xs space-y-2">
            <div>
              <p className="font-medium text-muted-foreground">Old note</p>
              <p className="whitespace-pre-wrap break-words">{notesDiff.oldText}</p>
            </div>
            <div>
              <p className="font-medium text-muted-foreground">New note</p>
              <p className="whitespace-pre-wrap break-words">{notesDiff.newText}</p>
            </div>
          </div>
        )}
        {log.performed_by_name && (
          <p className="text-xs text-muted-foreground">by {log.performed_by_name}</p>
        )}
      </div>
    </div>
  );
}

function ActivityLogSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3 py-2">
          <Skeleton className="h-4 w-4 shrink-0" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ProjectActivityLog({ projectId }: ProjectActivityLogProps) {
  const { data, isLoading } = useAuditLogs(projectId, { pageLength: 20 });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="h-4 w-4" />
          Activity Log
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <ActivityLogSkeleton />
        ) : !data?.logs || data.logs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No activity recorded yet</p>
        ) : (
          <ScrollArea className="h-[300px] pr-4">
            <div className="divide-y">
              {data.logs.map((log) => (
                <ActivityLogItem key={log.id} log={log} />
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
